import axiosInstance, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { Loading } from 'quasar'
import { BsSpinner } from '../../components/bs-component-bundle'
import { IsReviewerApp } from '../../utils'
import { useConfig } from '../config/configModule'
import authStoreGetters from '../authentication/store/getters'
import { _setAuthExpired } from '../authentication/store/mutations'
import {
  getConstraintErrorResponse,
  getErrorResponseFromCode,
  isAuthError,
  isBadRequest
} from '../../base/apiErrorHandler'

/**
 * Replaces AxiosService.
 *
 * Reads auth straight from the authentication store instead of an injected StoreService, and
 * config from configModule instead of an injected ConfigService — which is what lets the DI
 * registration go away.
 *
 * SSR note: one instance shared across requests is safe *because* the headers are computed
 * inside the request interceptor, which reads the request-scoped auth store at call time. The
 * instance holds only config and interceptors, no per-user state.
 */

/**
 * Whether the next request should raise the global loading overlay. Module-scope because
 * `globalLoader(false)` is called on a service and expected to affect the request that follows —
 * that was an instance field on the old singleton, so the scope is unchanged.
 */
let showGlobalLoader = true

let instance: AxiosInstance | undefined

/**
 * Marks a request as still waiting for the 200ms loader delay. Lives on the request config so it
 * is per request: the old code approximated that by building a whole new axios instance per
 * property access, which is the bug this module fixes.
 */
const LOADER_PENDING = '__bsLoaderPending'

/**
 * Marks a request as nothing to do with the overlay: it never raises it, and its answer never takes down
 * one another request raised. Set through `withoutLoader`.
 */
const WITHOUT_LOADER = '__bsWithoutLoader'

/**
 * A request config for work nobody is waiting on — a prefetch. `globalLoader(false)` cannot do this for
 * one request: it is module-wide, and the interceptor reads it a tick after the call, so switching it off
 * around a request switches the overlay off for everything else in flight. And Quasar's `Loading.hide()`
 * takes the overlay down for every caller, so a background answer would otherwise end the spinner of a
 * request the visitor is still waiting on (sc-28864).
 */
export const withoutLoader = (config: AxiosRequestConfig = {}): AxiosRequestConfig =>
  ({ ...config, [WITHOUT_LOADER]: true } as AxiosRequestConfig)

const isWithoutLoader = (config?: AxiosRequestConfig) =>
  (config as unknown as Record<string, unknown> | undefined)?.[WITHOUT_LOADER] === true

const hideLoader = (config?: AxiosRequestConfig) => {
  if (isWithoutLoader(config)) {
    return
  }

  if (config) {
    (config as unknown as Record<string, unknown>)[LOADER_PENDING] = false
  }

  Loading.hide()
}

/**
 * Rejections rather than resolutions, so the promise chain behaves; and Error instances, so
 * unhandled-rejection capture in Sentry has a stack.
 */
const handleErrorResponse = (r: any): Promise<never> => {
  return new Promise((__, reject) => {
    if (isAuthError(r)) {
      _setAuthExpired(true)
      /**
       * Flagged so list-load guards can swallow it — AUTH_EXPIRED already drives the UX, and
       * without this they surface as unhandled "invalidLogin" rejections in Sentry.
       */
      const authError: any = new Error(getErrorResponseFromCode('invalidLogin').message)
      authError.isAuthExpired = true

      return reject(authError)
    }

    if (isBadRequest(r)) {
      if (typeof r.response.data.message === 'string') {
        return reject(new Error(r.response.data.message))
      }

      try {
        const error = r.response.data.message[0]

        if (error !== void 0 && error.constraints !== void 0) {
          return reject(new Error(getConstraintErrorResponse(error.constraints).message))
        }

        if (error !== void 0) {
          return reject(new Error(error.message))
        }

        return reject(new Error(getErrorResponseFromCode(r.response.data.message.errorCode).message))
      } catch {
        return reject(new Error(getErrorResponseFromCode('somethingWentWrong').message))
      }
    }

    if (r?.response?.data?.message?.errorCode) {
      return reject(new Error(getErrorResponseFromCode(r.response.data.message.errorCode).message))
    }

    return reject(new Error(r?.response?.data?.message ?? r?.message ?? 'Request failed'))
  })
}

const createInstance = (): AxiosInstance => {
  const client = axiosInstance.create({
    baseURL: useConfig().config().axios.url,
    timeout: 60000,
    withCredentials: true
  })

  client.interceptors.request.use(config => {
    /**
     * Guarded on SERVER: the overlay is a browser affordance and there is nothing to show it on
     * during SSR. The old service had no such guard.
     */
    if (showGlobalLoader && !process.env.SERVER && !isWithoutLoader(config)) {
      (config as unknown as Record<string, unknown>)[LOADER_PENDING] = true

      setTimeout(() => {
        if ((config as unknown as Record<string, unknown>)[LOADER_PENDING]) {
          Loading.setDefaults({
            spinner: BsSpinner
          })
          Loading.show()
        }
      }, 200)
    }

    const authToken = authStoreGetters.authToken

    // @ts-expect-error axios' header typing is stricter than what it accepts
    config.headers = {
      ...config.headers,
      /**
       * Only when there is a token to send. Concatenating regardless put a literal `bearer null` on
       * every signed-out request from iOS, whose token is read from LocalStorage, and `bearer ` on
       * every request from web and Android, which keep the token in the cookie and store none
       * client-side. The server reads the cookie before this header, so leaving it out changes
       * nothing it can see. [sc-28630]
       */
      ...(authToken ? { Authorization: 'bearer ' + authToken } : {}),
      'CSRF-Token': authStoreGetters.csrfToken,
      'Referring-App': IsReviewerApp() ? 'reviewer' : 'publisher'
    }

    return config
  }, error => {
    hideLoader(error?.config)

    return handleErrorResponse(error)
  })

  client.interceptors.response.use(response => {
    hideLoader(response.config)

    // Only reachable on mobile; nginx answers this before a browser ever gets here.
    // `window.location` rather than the old `document.location` — the same object in a browser,
    // but the only one of the two that jsdom lets a spec stub.
    if (!process.env.SERVER && response.data?.offline) {
      window.location.href = '/#/maintenance'
    }

    return response
  }, error => {
    hideLoader(error?.config)

    return handleErrorResponse(error)
  })

  return client
}

export const useAxios = () => {
  /**
   * Created on first use, not at import time, so the instance picks up the baseURL that
   * `configModule` was initialised with during bootstrap.
   */
  const axios = (): AxiosInstance => {
    if (!instance) {
      instance = createInstance()
    }

    return instance
  }

  /**
   * Suppress or restore the loading overlay for subsequent requests. Returns the module bag so
   * the existing `globalLoader(false).get(...)` chaining style still reads the same.
   */
  const globalLoader = (enable: boolean) => {
    showGlobalLoader = enable

    return useAxios()
  }

  const redirectIfOffline = (router?: any): Promise<boolean> => {
    return axios().post('auth/live', {}, { timeout: 5000 }).then(r => {
      if (r.data.offline) {
        return router ? router.push('maintenance') : true
      }

      return false
    })
  }

  /**
   * Drops the memoised instance so the next call rebuilds it — needed when the config changes
   * (and by specs).
   */
  const reset = () => {
    instance = void 0
    showGlobalLoader = true
  }

  return {
    axios,
    globalLoader,
    redirectIfOffline,
    reset,
    handleErrorResponse
  }
}
